/**
 * ReportShareButton — "Share" for a report: copy its link, send it to team
 * members as an in-app chat message, or copy its Report ID.
 *
 * Uses existing flows only (ShareViaChatModal writes the same chat message it
 * always has); links are the portal's existing ?post=<id> deep links.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ShareViaChatModal from '../ShareViaChatModal/ShareViaChatModal';
import { PORTAL_CARDS } from '../../constants/portalCards';
import { reportId, reportLink, copyText } from '../../utils/reportId';
import '../../design/v2.css';

export function resolvePortal(portalId, fallbackColor) {
  const pc = PORTAL_CARDS.find(p => p.id === portalId);
  return {
    label: pc?.label || (portalId ? portalId.charAt(0).toUpperCase() + portalId.slice(1) : 'Report'),
    color: pc?.color || fallbackColor || '#0e9cc0',
    path:  pc?.path  || (portalId ? `/${portalId}` : '/'),
  };
}

export default function ReportShareButton({
  post, portalId, portalLabel, portalColor, portalPath, currentUser,
  variant = 'icon', // 'icon' | 'button'
  color,
}) {
  const [open, setOpen]       = useState(false);
  const [chat, setChat]       = useState(false);
  const [copied, setCopied]   = useState('');
  const [pos, setPos]         = useState(null);
  const btnRef  = useRef(null);
  const menuRef = useRef(null);

  const p     = resolvePortal(portalId, portalColor);
  const label = portalLabel || p.label;
  const accent = portalColor || p.color;
  const path  = portalPath || p.path;
  const rid   = reportId(post, portalId);
  const link  = reportLink(post, path);

  useEffect(() => {
    if (!open) return undefined;
    const close = e => {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  function toggle(e) {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    const r = btnRef.current.getBoundingClientRect();
    const W = 220, H = 190;
    const left = Math.max(8, Math.min(window.innerWidth - W - 8, r.right - W));
    const top  = r.bottom + H + 8 > window.innerHeight ? Math.max(8, r.top - H - 6) : r.bottom + 6;
    setPos({ left, top, width: W });
    setOpen(true);
  }

  async function doCopy(kind) {
    const ok = await copyText(kind === 'id' ? rid : link);
    setCopied(ok ? kind : 'fail');
    setTimeout(() => { setCopied(''); setOpen(false); }, 900);
  }

  async function doNative() {
    try { await navigator.share({ title: post.title || rid, text: `${rid} — ${post.title || 'Report'}`, url: link }); } catch (_) {}
    setOpen(false);
  }

  const canNative = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const stop = e => e.stopPropagation();

  return (
    <>
      {variant === 'button' ? (
        <button ref={btnRef} type="button" className="smc2-btn" onClick={toggle} title="Share report"
          aria-haspopup="menu" aria-expanded={open}>
          <ShareIcon /> SHARE
        </button>
      ) : (
        <button ref={btnRef} type="button" onClick={toggle} title="Share report" aria-haspopup="menu" aria-expanded={open}
          style={{ background:'none', border:'none', cursor:'pointer', padding:'2px 4px', lineHeight:1,
            display:'flex', alignItems:'center', gap:4, color: open ? accent : (color || 'var(--smc-tx3)'),
            fontFamily:"'Share Tech Mono', monospace", fontSize:9, letterSpacing:'0.12em' }}>
          <ShareIcon /> SHARE
        </button>
      )}

      {open && pos && createPortal(
        <div ref={menuRef} role="menu" className="smc2-dropdown" onClick={stop}
          style={{ position:'fixed', left:pos.left, top:pos.top, right:'auto', width:pos.width, zIndex:10050, '--dept': accent }}>
          <div className="smc2-menu-label">{rid}</div>
          <button type="button" role="menuitem" className="smc2-menu-item" onClick={() => doCopy('link')}>
            {copied === 'link' ? '✓ LINK COPIED' : '🔗 COPY LINK'}
          </button>
          <button type="button" role="menuitem" className="smc2-menu-item" onClick={() => { setOpen(false); setChat(true); }}>
            💬 SEND IN CHAT
          </button>
          <button type="button" role="menuitem" className="smc2-menu-item" onClick={() => doCopy('id')}>
            {copied === 'id' ? '✓ ID COPIED' : '# COPY REPORT ID'}
          </button>
          {canNative && (
            <button type="button" role="menuitem" className="smc2-menu-item" onClick={doNative}>↗ SHARE VIA…</button>
          )}
          {copied === 'fail' && <div className="smc2-menu-label" style={{ color:'var(--smc-rd)' }}>COPY FAILED — {link}</div>}
        </div>,
        document.body,
      )}

      {chat && (
        <span onClick={stop}>
          <ShareViaChatModal
            post={post}
            portalId={portalId}
            portalLabel={label}
            portalColor={accent}
            portalPath={path}
            currentUser={currentUser}
            onClose={() => setChat(false)}
          />
        </span>
      )}
    </>
  );
}

function ShareIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <circle cx="16" cy="4" r="2.2"/><circle cx="4" cy="10" r="2.2"/><circle cx="16" cy="16" r="2.2"/>
      <line x1="6" y1="9" x2="14" y2="5"/><line x1="6" y1="11" x2="14" y2="15"/>
    </svg>
  );
}
